import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"

const STORAGE_KEY = "exdev-optional-content-v1"
const CHANGE_EVENT = "exdev-privacy-change"
const MAX_AGE = 180 * 24 * 60 * 60 * 1000
let sessionChoice = null

export const readChoice = () => {
  if (typeof window === "undefined") return null
  try {
    const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY))
    if (
      saved &&
      ["accepted", "rejected"].includes(saved.choice) &&
      Number.isFinite(saved.savedAt) &&
      Date.now() >= saved.savedAt &&
      Date.now() - saved.savedAt < MAX_AGE
    ) {
      return saved.choice
    }
  } catch (error) {
    // Privacy choices still work for this page when storage is unavailable.
    return sessionChoice
  }
  return sessionChoice
}

export const saveChoice = (choice) => {
  if (!["accepted", "rejected"].includes(choice)) return
  const previousChoice = readChoice()
  sessionChoice = choice
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, savedAt: Date.now() })
    )
  } catch (error) {
    // Do not make access to browser storage a requirement for rejecting embeds.
  }
  window.dispatchEvent(new window.Event(CHANGE_EVENT))
  // Unmounting an iframe does not stop the already-loaded Google Maps SDK.
  if (previousChoice === "accepted" && choice === "rejected") {
    window.location.reload()
  }
}

export const useOptionalContent = () => {
  // SSR and the first client render must always block optional content.
  const [choice, setChoice] = useState(null)
  useEffect(() => {
    const update = () => setChoice(readChoice())
    const sync = (event) => {
      if (event.key === STORAGE_KEY || event.key === null) {
        sessionChoice = null
        const nextChoice = readChoice()
        if (choice === "accepted" && nextChoice !== "accepted") {
          window.location.reload()
        }
        setChoice(nextChoice)
      }
    }
    update()
    window.addEventListener(CHANGE_EVENT, update)
    window.addEventListener("storage", sync)
    return () => {
      window.removeEventListener(CHANGE_EVENT, update)
      window.removeEventListener("storage", sync)
    }
  }, [choice])
  return choice
}

const Panel = styled.section`
  padding: 20px;
  color: #021f4b;
  background: #fff;
  border: 1px solid #ccd4df;
  border-radius: 8px;
  font: 16px/1.5 Inter, Arial, sans-serif;
  text-align: left;

  p {
    margin: 8px 0 16px;
  }

  a {
    color: #021f4b;
    text-decoration: underline;
  }

  button,
  a {
    &:focus-visible {
      outline: 3px solid #0068cc;
      outline-offset: 3px;
    }
  }
`

const Banner = styled(Panel)`
  position: fixed;
  right: 16px;
  bottom: 16px;
  left: 16px;
  z-index: 2147483000;
  width: auto;
  max-width: 760px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  margin: 0 auto;
  box-shadow: 0 4px 24px rgba(2, 31, 75, 0.2);
  opacity: 0;
  transform: translateY(16px);
  visibility: hidden;
  pointer-events: none;
  transition: opacity 240ms ease, transform 240ms ease,
    visibility 0s linear 240ms;

  &[data-open="true"] {
    opacity: 1;
    transform: translateY(0);
    visibility: visible;
    pointer-events: auto;
    transition-delay: 0s;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
    transition: none;
  }
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const ChoiceButton = styled.button`
  min-height: 44px;
  padding: 10px 16px;
  border: 1px solid #021f4b;
  border-radius: 4px;
  color: #fff;
  background: #021f4b;
  cursor: pointer;
  font: inherit;
`

const SettingsButton = styled(ChoiceButton)`
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 9999;
  font: 14px/1.4 Inter, Arial, sans-serif;
  box-shadow: 0 2px 8px rgba(2, 31, 75, 0.2);

  &:focus-visible {
    outline: 3px solid #0068cc;
    outline-offset: 3px;
  }
`

const ChoiceActions = ({ onChoose }) => (
  <Actions>
    <ChoiceButton type="button" onClick={() => onChoose("accepted")}>
      Accept optional content
    </ChoiceButton>
    <ChoiceButton type="button" onClick={() => onChoose("rejected")}>
      Reject optional content
    </ChoiceButton>
  </Actions>
)

ChoiceActions.propTypes = { onChoose: PropTypes.func.isRequired }

export const PrivacyControls = () => {
  const choice = useOptionalContent()
  const [isOpen, setIsOpen] = useState(false)
  const settingsRef = useRef(null)
  useEffect(() => {
    setIsOpen(readChoice() === null)
    const dismiss = () => setIsOpen(false)
    window.addEventListener(CHANGE_EVENT, dismiss)
    return () => window.removeEventListener(CHANGE_EVENT, dismiss)
  }, [])
  const close = () => {
    setIsOpen(false)
    if (settingsRef.current) settingsRef.current.focus()
  }
  const choose = (value) => {
    saveChoice(value)
    close()
  }
  return (
    <>
      <SettingsButton
        ref={settingsRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls="exdev-privacy-banner"
        onClick={() => (isOpen ? close() : setIsOpen(true))}
      >
        Privacy settings
      </SettingsButton>
      <Banner
        id="exdev-privacy-banner"
        aria-label="Cookie and privacy choices"
        aria-hidden={!isOpen}
        inert={isOpen ? undefined : ""}
        data-open={isOpen}
      >
        <strong>Cookies and optional content</strong>
        <p>
          We use browser storage for essential site functions and to remember
          your choice. With your permission, we load Google Maps and YouTube
          videos. Google may use cookies and collect information such as your IP
          address for these services. Rejecting keeps these embeds blocked; you
          can still use the site and open external links.
        </p>
        <ChoiceActions onChoose={choose} />
        <p>
          Change your choice anytime using Privacy settings. Rejecting after
          accepting reloads this page. <a href="/en/privacy">Privacy policy</a>
        </p>
        {choice !== null && (
          <button type="button" onClick={close}>
            Close settings
          </button>
        )}
      </Banner>
    </>
  )
}

export const ConsentPlaceholder = ({ service, href, className, style }) => (
  <Panel className={className} style={style} aria-label={`${service} blocked`}>
    <strong>{service} is blocked</strong>
    <p>
      Accept optional content to enable Google Maps and YouTube videos. Google
      may use cookies and receive information such as your IP address. You can
      reject and open the content on the provider&apos;s website instead.
    </p>
    <ChoiceActions onChoose={saveChoice} />
    <p>
      <a href={href} target="_blank" rel="noopener noreferrer">
        Open in {service}
      </a>
      {" · "}
      <a href="/en/privacy">Privacy policy</a>
    </p>
  </Panel>
)

ConsentPlaceholder.propTypes = {
  service: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
}
ConsentPlaceholder.defaultProps = { className: undefined, style: undefined }

export const withMapConsent = (Component) => {
  const MapWithConsent = (props) => {
    const choice = useOptionalContent()
    if (choice === "accepted") return <Component {...props} />
    const { projectAddress, sectionData } = props
    const address = projectAddress || (sectionData && sectionData.name)
    const href = address
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          address
        )}`
      : "https://www.google.com/maps"
    return <ConsentPlaceholder service="Google Maps" href={href} />
  }
  MapWithConsent.propTypes = {
    projectAddress: PropTypes.string,
    sectionData: PropTypes.object,
  }
  MapWithConsent.defaultProps = {
    projectAddress: undefined,
    sectionData: undefined,
  }
  return MapWithConsent
}
